import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { ApiError } from '../../api/client'
import {
  chatGeneralQuery,
  chatShellQuery,
  chatTicketTypeStatusesQuery,
  chatTicketsQuery,
  rebuildChatConfiguration,
  saveChatGeneral,
  saveChatTickets,
  type ChatGeneralSettings,
  type ChatTicketSetting,
  type ChatTickets,
} from '../../api/settingsEaseedeskChat'
import { LoadFailed } from '../../ui/LoadFailed'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Chat settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/chat/settings/
 * index.php` + `general_sp.php` + `general.php` + `tickets.php`, si gest cu
 * gest fata de `index-1.js`, `general_sp-1.js`, `general-1.js` si `tickets-1.js`.
 *
 * CELE DOUA MACHETE DE SETARI GENERALE sunt una singura aici: se deosebeau
 * doar prin doua randuri - ceasul de instiintare, care e numai al furnizorului,
 * si sablonul de chat, care e numai al unei firme. Serverul spune care din ele
 * are rost (`has_user_notifications`, `has_template`).
 *
 * Butonul "Refresh cron" e aici, si se vede numai la furnizor - ca in portal,
 * unde statea in `general_sp.php`, nu si in `general.php`. Cheama serverul de
 * cronuri prin `App\Platform\CronServer`, si spune daca a ajuns: in portal,
 * cand nu ajungea, cererea murea cu "Curl failed" si pagina ramanea rupta.
 */

const LI_STYLE: React.CSSProperties = {
  padding: '10px 20px',
  marginBottom: 4,
  borderRadius: 4,
  borderWidth: '1px 6px',
  borderColor: '#ddd',
  borderStyle: 'solid',
}

function Row({ title, hint, children, right }: { title: string; hint?: string; children?: React.ReactNode; right?: React.ReactNode }) {
  return (
    <li style={LI_STYLE}>
      <div className="row">
        <div className="col-md-4">
          <h5 className="pull-left" style={{ margin: 0, padding: '5px 0', marginLeft: 20 }}>
            {title}{' '}
            {hint !== undefined && (
              <i className="fas fa-info-circle color-blue font-14" style={{ cursor: 'default' }} title={hint}>
                {' '}
              </i>
            )}
          </h5>
        </div>
        <div className="col-md-1"></div>
        <div className="col-md-5">{children}</div>
        <div className="col-md-2">{right !== undefined && <div className="pull-right">{right}</div>}</div>
      </div>
    </li>
  )
}

function GeneralSection({ company }: { company: number }) {
  const queryClient = useQueryClient()
  const data = useQuery(chatGeneralQuery(company))
  const [form, setForm] = useState<ChatGeneralSettings | null>(null)
  const [template, setTemplate] = useState('')

  useEffect(() => {
    if (!data.data) return
    setForm(data.data.settings)
    setTemplate(data.data.template)
  }, [data.data])

  const save = useMutation({
    mutationFn: () => saveChatGeneral(company, form as ChatGeneralSettings, template),
    onSuccess: () => {
      toast('Chat settings updated.', 'Chat settings')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'chat_settings', 'general', company] })
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (data.error) {
    return <LoadFailed what="The general chat settings" error={data.error} onRetry={() => void data.refetch()} />
  }
  if (!data.data || form === null) return <Spinner />
  const set = (patch: Partial<ChatGeneralSettings>) => setForm({ ...form, ...patch })

  return (
    <form
      className="chat_general_settings"
      method="post"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      {data.data.has_user_notifications && <RefreshCron />}

      <ul>
        <Row title="Chat title">
          <input
            type="text"
            className="form-control"
            name="settings[chat_title]"
            value={form.chat_title}
            onChange={(e) => set({ chat_title: e.target.value })}
          />
        </Row>

        {(
          [
            ['Button color', 'button_color'],
            ['Received message color', 'received_message_color'],
            ['Sent message color', 'sent_message_color'],
          ] as Array<[string, 'button_color' | 'received_message_color' | 'sent_message_color']>
        ).map(([label, field]) => (
          <Row key={field} title={label}>
            <input
              type="color"
              className="form-control"
              name={`settings[${field}]`}
              value={form[field] === '' ? '#000000' : form[field]}
              onChange={(e) => set({ [field]: e.target.value } as Partial<ChatGeneralSettings>)}
            />
          </Row>
        ))}

        {data.data.has_user_notifications && (
          <Row
            title="User notification time"
            hint="The time, in minutes, after which user will be notified if there are any unread messages"
            right={
              <input
                type="checkbox"
                name="settings[user_notifications][checked]"
                className="notification-switch"
                checked={form.user_notifications === 1}
                onChange={(e) => set({ user_notifications: e.target.checked ? 1 : 0 })}
              />
            }
          >
            <input
              type="number"
              className="form-control input-sm input-number"
              name="settings[user_notifications][time]"
              min={2}
              value={form.user_notifications_time}
              onChange={(e) => set({ user_notifications_time: Number(e.target.value) })}
            />
          </Row>
        )}

        <Row
          title="Open chat windows automatically"
          hint="if a technician is trying to get in touch with customer through chat, open chat automatically"
          right={
            <input
              type="checkbox"
              name="settings[open_chat_window][checked]"
              className="notification-switch"
              checked={form.open_chat_window === 1}
              onChange={(e) => set({ open_chat_window: e.target.checked ? 1 : 0 })}
            />
          }
        >
          <select
            className="form-control chat-consent"
            name="settings[open_chat_window][consent]"
            value={form.open_chat_window_consent}
            onChange={(e) => set({ open_chat_window_consent: Number(e.target.value) })}
          >
            <option value={1}>Ask for consent</option>
            <option value={0}>Do not ask for consent</option>
          </select>
        </Row>

        <Row title="Automated user name">
          <input
            type="text"
            className="form-control"
            name="settings[automated_user]"
            value={form.automated_user}
            onChange={(e) => set({ automated_user: e.target.value })}
          />
        </Row>

        {data.data.has_template && (
          <Row title="Chat template">
            <select
              className="form-control chat-template"
              name="settings[template]"
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              required
            >
              <option value="">Select..</option>
              {data.data.templates.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </Row>
        )}
      </ul>

      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-md-12">
          <button className="btn btn-primary pull-right update-chat-general-settings" type="submit" disabled={save.isPending}>
            Update
          </button>
        </div>
      </div>
    </form>
  )
}

/** Casuta de alegere a unei setari: lista vine dupa `select_field`. */
function SettingSelect({
  setting,
  data,
  value,
  onChange,
}: {
  setting: ChatTicketSetting
  data: ChatTickets
  value: number
  onChange(next: number): void
}) {
  const field = setting.select_field
  if (field === null) return null

  const list =
    field === 'ticket_types'
      ? data.ticket_types
      : field === 'ticket_statuses'
        ? data.ticket_statuses
        : field === 'ticket_priorities'
          ? data.ticket_priorities
          : field === 'groups'
            ? data.groups
            : []

  return (
    <select
      className="form-control"
      name={`settings[${setting.setting_id}][value]`}
      value={value === 0 ? '' : String(value)}
      onChange={(e) => onChange(Number(e.target.value))}
      required
    >
      <option value="">Select..</option>
      {list.map((row) => (
        <option key={row.ID} value={String(row.ID)}>
          {row.name}
        </option>
      ))}
    </select>
  )
}

function TicketsSection({ company, chatSource }: { company: number; chatSource: number }) {
  const queryClient = useQueryClient()
  const data = useQuery(chatTicketsQuery(company, chatSource))
  const [rows, setRows] = useState<Array<{ setting_id: number; value: number; checked: boolean }>>([])

  useEffect(() => {
    if (!data.data) return
    setRows(
      data.data.settings.map((s) => ({
        setting_id: s.setting_id,
        value: s.select_value ?? 0,
        checked: s.checked === 1,
      })),
    )
  }, [data.data])

  /* Casuta de stari se umple din fluxul tipului ales in casuta de tip, si se
     cere din nou la fiecare schimbare a lui - ca `getTicketStatus()` din
     vechea macheta. Fara casuta de tip, raman starile din raspuns. */
  const typeSettings = (data.data?.settings ?? [])
    .filter((s) => s.select_field === 'ticket_types')
    .map((s) => s.setting_id)
  const statusSettings = (data.data?.settings ?? [])
    .filter((s) => s.select_field === 'ticket_statuses')
    .map((s) => s.setting_id)
  const selectedType = rows.find((r) => typeSettings.includes(r.setting_id))?.value ?? 0
  const typeStatuses = useQuery(chatTicketTypeStatusesQuery(selectedType))

  const save = useMutation({
    mutationFn: () => saveChatTickets(company, chatSource, rows),
    onSuccess: () => {
      toast('Chat ticket settings updated.', 'Chat settings')
      void queryClient.invalidateQueries({
        queryKey: ['settings', 'easeedesk', 'chat_settings', 'tickets', company, chatSource],
      })
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  if (data.error) {
    return <LoadFailed what="The ticket settings" error={data.error} onRetry={() => void data.refetch()} />
  }
  if (!data.data) return <Spinner />

  const patch = (settingId: number, next: Partial<{ value: number; checked: boolean }>) =>
    setRows((prev) => {
      const changedType =
        typeSettings.includes(settingId) &&
        next.value !== undefined &&
        prev.find((r) => r.setting_id === settingId)?.value !== next.value
      return prev.map((r) =>
        r.setting_id === settingId
          ? { ...r, ...next }
          : changedType && statusSettings.includes(r.setting_id)
            ? { ...r, value: 0 }
            : r,
      )
    })

  const listData: ChatTickets =
    typeSettings.length === 0
      ? data.data
      : {
          ...data.data,
          ticket_statuses: selectedType > 0 ? (typeStatuses.data?.statuses ?? []) : [],
        }

  return (
    <form
      className="chat_tickets_settings"
      method="post"
      onSubmit={(e) => {
        e.preventDefault()
        save.mutate()
      }}
    >
      <ul>
        {data.data.settings.map((setting) => {
          const row = rows.find((r) => r.setting_id === setting.setting_id)
          return (
            <Row
              key={setting.setting_id}
              title={setting.setting_name}
              hint={setting.info === '' ? undefined : setting.info}
              right={
                <input
                  type="checkbox"
                  name={`settings[${setting.setting_id}][checked]`}
                  checked={row?.checked ?? false}
                  onChange={(e) => patch(setting.setting_id, { checked: e.target.checked })}
                />
              }
            >
              <SettingSelect
                setting={setting}
                data={listData}
                value={row?.value ?? 0}
                onChange={(next) => patch(setting.setting_id, { value: next })}
              />
            </Row>
          )
        })}
      </ul>

      <div className="row" style={{ marginTop: 20 }}>
        <div className="col-md-12">
          <button className="btn btn-primary pull-right update-chat-tickets-settings" type="submit" disabled={save.isPending}>
            Update
          </button>
        </div>
      </div>
    </form>
  )
}

/**
 * "Refresh cron": spune serverului de cronuri ce sa faca cu instiintarile din
 * chat, dupa ce s-au schimbat setarile. Numai la furnizor - ceasul de
 * instiintare e al lui, nu al unei firme.
 */
function RefreshCron() {
  const refresh = useMutation({
    mutationFn: () => rebuildChatConfiguration(),
    onSuccess: (answer) =>
      answer.result === 'done'
        ? toast('Chat settings cron refreshed.', 'Chat settings')
        : toast(answer.message || 'The cron server did not answer. The settings are saved.', 'Chat settings', 'error'),
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <section className="settings">
      <div className="row">
        <div className="col-md-3">
          <div className="form-group row">
            <div className="col-md-12">
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={refresh.isPending}
                onClick={() => refresh.mutate()}
              >
                <i className="fas fa-sync-alt" aria-hidden="true"></i> Refresh cron
              </button>
              <span className="help-block">Refresh chat settings cron after every change.</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export function ChatSettingsPage() {
  const shell = useQuery(chatShellQuery())
  const [company, setCompany] = useState(0)
  const [chatSource, setChatSource] = useState(0)

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed what="Chat settings" error={shell.error} onRetry={() => void shell.refetch()} />
  }
  if (!shell.data) return <Spinner />

  return (
    <div>
      <h4>
        <strong>Chat</strong> settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage settings and tickets opened from <strong>agent chat.</strong>
      </label>
      <hr />

      <section className="settings">
        <div className="row" style={{ marginBottom: 20 }}>
          <div className="col-md-3">
            <select
              className="form-control input-sm"
              name="company_settings"
              value={company}
              onChange={(e) => setCompany(Number(e.target.value))}
            >
              <option value={0}>All companies</option>
              {shell.data.companies.map((row) => (
                <option key={row.ID} value={row.ID}>
                  {row.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ width: 300 }}>General settings</h5>
          </div>
          <div className="col-md-10">
            <div className="general-group">
              <GeneralSection company={company} />
            </div>
          </div>
        </div>
        <hr className="dotted" />
      </section>

      <section className="settings">
        <div className="row">
          <div className="col-md-2">
            <h5 style={{ width: 300 }}>Tickets settings</h5>
          </div>
          <div className="col-md-10" style={{ marginBottom: 10 }}>
            <select
              className="form-control input-sm"
              name="chat_source"
              value={chatSource}
              onChange={(e) => setChatSource(Number(e.target.value))}
            >
              <option value={0}>All chats</option>
              {shell.data.sources.map((row) => (
                <option key={row.chat_source} value={row.chat_source}>
                  {row.source_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-md-2"></div>
          <div className="col-md-10">
            <div className="tickets-group">
              <TicketsSection company={company} chatSource={chatSource} />
            </div>
          </div>
        </div>
        <hr className="dotted" />
      </section>
    </div>
  )
}
